import {
  CaptionedNichePersonImage,
  DetailsPanel,
  RetrospectiveTypeIcon,
} from '@/components/controls';
import { useNicheOccurrence, useOptionLookup } from '@/hooks';
import {
  Avatar,
  Badge,
  Card,
  CardHeader,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';

export function PublicSafetyEvent({
  id,
  background,
  color,
  reportedTime,
  startTime,
  endTime,
  statusCode,
  summary,
  typeId,
}) {
  const {
    query: { data: occurrence },
  } = useNicheOccurrence(id, {
    addresses: true,
    officers: true,
    people: true,
    vehicles: true,
  });
  const publicSafetyEventTypes = useOptionLookup('publicSafetyEventType');
  const occurrenceStatuses = useOptionLookup('occurrenceStatus');
  const officerClassifications = useOptionLookup('officerClassification');

  return (
    <Card sx={{ m: 1 }}>
      <title>
        {['IR3', 'Public Safety/Welfare Incidents', id]
          .filter(Boolean)
          .join(' | ')}
      </title>
      <CardHeader
        avatar={
          <Badge>
            <Avatar
              style={{
                background,
                color,
              }}
            >
              <RetrospectiveTypeIcon type="publicSafetyEvents" />
            </Avatar>
          </Badge>
        }
        title={id}
        subheader={publicSafetyEventTypes[typeId]}
        action={<Chip label={occurrenceStatuses[statusCode] ?? statusCode} />}
      />
      <Stack spacing={2} sx={{ p: 2, pt: 0 }}>
        <Typography variant="body2">{summary}</Typography>
        <DetailsPanel
          entries={[
            {
              label: 'Reported',
              value: reportedTime
                ? format(reportedTime, 'dd/MM/yyyy H:mm')
                : '',
            },
            {
              label: 'Occurred',
              value: [
                startTime && format(startTime, 'dd/MM/yyyy H:mm'),
                endTime && format(endTime, 'dd/MM/yyyy H:mm'),
              ]
                .filter(Boolean)
                .join(' - '),
            },
          ]}
        />
        {occurrence?.people?.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
            {occurrence.people.map((person) => (
              <CaptionedNichePersonImage person={person} key={person.id} />
            ))}
          </Stack>
        )}
        {occurrence?.officers?.length > 0 && (
          <DetailsPanel
            title="Officers"
            entries={occurrence.officers.map(
              ({ collarNumber, classificationCodes }) => ({
                label: collarNumber,
                value: classificationCodes
                  .map((code) => officerClassifications[code] ?? code)
                  .join(', '),
              }),
            )}
          />
        )}
        {occurrence?.vehicles?.length > 0 && (
          <DetailsPanel
            title="Vehicles"
            entries={occurrence.vehicles.map(
              ({ registration, make, model, description }) => ({
                label: registration || 'Unknown Registration',
                value: [make, model, description].filter(Boolean).join(' '),
              }),
            )}
          />
        )}
        {occurrence?.addresses?.length > 0 && (
          <DetailsPanel
            title="Addresses"
            entries={occurrence.addresses.map(({ type, label }) => ({
              label: type || 'Address',
              value: label ?? '',
            }))}
          />
        )}
      </Stack>
    </Card>
  );
}
